import type { RaceStatus } from "./schema/race";

/**
 * 대회 시점(Race phase) 도출 — 저장 `status` 는 시간이 지나도 갱신되지 않아 stale 해진다.
 * 그래서 시점 상태는 저장값을 신뢰하지 않고 현재 시각·대회일·접수 일정으로 read-time 에
 * 보정해 도출한다. web·API·테스트가 이 단일 규칙을 공유한다.
 *
 * 배경: docs/adr/0001-race-phase-read-time-derivation.md,
 *       docs/adr/0002-promote-race-phase-to-shared.md.
 *
 * 날짜 경계 기준 타임존은 Asia/Seoul 로 고정한다. 도메인이 한국 대회 일정이고,
 * host-local Date 연산(`new Date(y,m,d)` / `getFullYear` 등)은 런타임(브라우저=KST /
 * Cloudflare Workers=UTC)에 따라 "오늘"이 최대 9시간(=하루) 어긋나기 때문이다.
 * `now` 는 테스트를 위해 주입 가능.
 */

const KST_DATE_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Seoul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Date → Asia/Seoul 기준 YYYYMMDD 정수. 같은 날이면 같은 값(시각은 무시).
 * 정수 비교가 `stripTime` 의 자정 Date 비교(`<`, `<=`)를 동치로 대체한다.
 */
function kstDayNum(d: Date): number {
  // "en-CA" 로케일은 "YYYY-MM-DD" 형식을 보장한다.
  const s = KST_DATE_FMT.format(d);
  return Number(s.slice(0, 4) + s.slice(5, 7) + s.slice(8, 10));
}

function parseDate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * normalizeStatus 가 보는 최소 입력. 도메인 `Race` 가 이를 구조적으로 만족하고,
 * registration 컬럼이 없는 부분 조회(예: user_races JOIN — date·status 만 가져옴)도
 * 받을 수 있다. registration 이 없으면 "대회일 지남 → completed" 규칙만 적용된다.
 */
export interface RacePhaseInput {
  date: string;
  status: RaceStatus;
  registration?: { startDate?: string; endDate?: string };
}

/**
 * 원본 `status` 가 실제 시간·접수 일정과 어긋나는 경우 read-time 에 보정한다.
 * 배너에 "접수중"이 붙어 있지만 실제 regEnd 가 지난 경우 같은 모순을 차단해서
 * 정렬·필터·뱃지·D-day 캡션이 한 방향으로 말하게 만든다.
 *
 * 보정 순서:
 *   1) 대회일이 오늘보다 지났으면 무조건 completed
 *   2) regStart / regEnd 가 둘 다 유효하면 오늘 위치로 bucket 재계산
 *   3) 한쪽만 유효해도 명백한 모순만 바로잡는다 (open·upcoming 만)
 */
export function normalizeStatus(race: RacePhaseInput, now: Date = new Date()): RaceStatus {
  const today = kstDayNum(now);

  const raceDate = parseDate(race.date);
  if (raceDate && kstDayNum(raceDate) < today) {
    return "completed";
  }

  if (race.status === "completed") return "completed";

  const regStart = parseDate(race.registration?.startDate);
  const regEnd = parseDate(race.registration?.endDate);

  if (regStart && regEnd) {
    const s = kstDayNum(regStart);
    const e = kstDayNum(regEnd);
    if (today < s) return "upcoming";
    if (today <= e) return "open";
    return "closed";
  }

  if (regEnd && kstDayNum(regEnd) < today) {
    // open / upcoming 인데 접수 끝이 지났다 → closed (race 는 아직 미래)
    if (race.status === "open" || race.status === "upcoming") return "closed";
  }

  if (regStart && race.status === "upcoming" && kstDayNum(regStart) <= today) {
    // 예정이라 적혀 있지만 접수 시작일은 이미 지났다 → open 으로 승격
    return "open";
  }

  return race.status;
}

/**
 * race 객체의 status 를 read-time 보정값으로 치환한 새 객체를 돌려준다.
 * 모든 보정 지점(어댑터 withPhase, user_races·추천·인기 직접 호출)이 이 한 함수를
 * 거치게 해 보정 모양을 하나로 유지한다. (ADR-0002 / Task 122)
 */
export function applyPhase<T extends RacePhaseInput>(race: T, now?: Date): T {
  return { ...race, status: normalizeStatus(race, now) };
}
